import './NylasScheduler.scss'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  isNylasSuccessResponse,
  NylasSchedulerBookingParticipant,
  ThemeConfig,
  Timeslot,
} from '@nylas/core'
import { NylasScheduling } from '@nylas/react'
import {
  NylasSchedulerStoreState,
  ObservableMap,
  SchedulerEventOverride,
} from '@nylas/web-elements'
import { NylasSchedulerConnectorInterface } from '@nylas/web-elements/dist/types/connector/connector-interface'
import { faRotateRight } from '@rq-ratings/pro-solid-svg-icons'
import { useMutation } from '@tanstack/react-query'
import React, { useRef, useState } from 'react'
import { Button, Card } from 'react-bootstrap'

import { useCaptureErrorShownToUserEvent } from '../../../hooks/useCaptureErrorShownToUserEvent'
import Collapsible from '../Collapsible'
import OpenChatButton from '../OpenChatButton'
import NylasSchedulerError from './NylasSchedulerError'
import { BookingDetails, ClientDetails } from './types'

interface Props {
  schedulerConfigId: string
  className?: string
  primaryParticipant: NylasSchedulerBookingParticipant
  guests: NylasSchedulerBookingParticipant[]
  onBookingComplete: (completedBooking: BookingDetails) => void
  clientDetails: ClientDetails
}

type BookTimeslotMutationVariables = {
  event: CustomEvent<{
    timeslot: Timeslot
    host: HTMLNylasTimeslotPickerElement
  }>
  connector: NylasSchedulerConnectorInterface
}

const NylasScheduler: React.FC<Props> = ({
  schedulerConfigId,
  className,
  primaryParticipant,
  guests,
  onBookingComplete,
  clientDetails,
}) => {
  const captureErrorShownToUserEvent = useCaptureErrorShownToUserEvent()
  const [schedulerError, setSchedulerError] = useState<{
    title: string
    description?: string
  } | null>(null)
  const [dismissedErrorCount, setDismissedErrorCount] = useState(0)
  const nylasSchedulingElementRef = useRef<HTMLNylasSchedulingElement>(null)

  const bookTimeslotMutation = useMutation({
    mutationFn: async ({ event, connector }: BookTimeslotMutationVariables) => {
      const response = await connector.scheduler.bookTimeslot({
        primaryParticipant,
        guests,
        additionalFields: buildAdditionalFields(),
        timezone: 'Europe/London',
      })

      if (!isNylasSuccessResponse(response)) {
        throw response.error
      }

      const booking = response.data
      const nylasSchedulerStore = await getNylasSchedulerStore()

      onBookingComplete({
        nylasBookingId: booking.booking_id,
        startTime: event.detail.timeslot.start_time,
        endTime: event.detail.timeslot.end_time,
        bookedWith: {
          name: booking.organizer.name,
          email: booking.organizer.email,
        },
        clientDetails: primaryParticipant,
        guests,
        schedulerConfigId,
        timezone: nylasSchedulerStore?.state.selectedTimezone,
      })
    },

    onError: (error) => {
      captureErrorShownToUserEvent({
        message: 'Error booking Nylas timeslot',
        extra: { error },
      })
    },

    retry: (failureCount, error) => {
      return failureCount === 0 && isAdditionalFieldError(error)
    },
  })

  async function getNylasSchedulerStore(): Promise<
    ObservableMap<NylasSchedulerStoreState> | undefined
  > {
    if (!nylasSchedulingElementRef.current) {
      return
    }

    return await nylasSchedulingElementRef.current.getNylasSchedulerStore()
  }

  function buildAdditionalFields(): Record<string, string> | undefined {
    const hasAlreadyFailedBecauseOfAdditionalFieldError =
      !!bookTimeslotMutation.failureReason &&
      isAdditionalFieldError(bookTimeslotMutation.failureReason)

    if (hasAlreadyFailedBecauseOfAdditionalFieldError) {
      return
    }

    // We use this field to include the client's details in the calendar event
    // description. In calendar invite descriptions on Google Calendar and Outlook
    // Calendar, whitespace (including new lines) are rendered but HTML attributes
    // aren't.
    const rqDescription = `<b>Client details:</b> \n${[
      clientDetails.fullName,
      clientDetails.telephone,
      clientDetails.email,
    ]
      .filter((part) => !!part)
      .join(',\n')}${
      clientDetails.circumstances
        ? `
        <br><b>Circumstances</b>: \n${clientDetails.circumstances}`
        : ''
    }${
      clientDetails.services.length > 0
        ? `
        <br><b>Services</b>: \n${clientDetails.services.join(', ')}`
        : ''
    }
    `.trim()

    return { rq_description: rqDescription }
  }

  function isAdditionalFieldError(error: Error): boolean {
    return /Additional field 'rq_description' not found/i.test(error.message)
  }

  if (!schedulerConfigId) {
    return <NylasSchedulerError />
  }

  const handleTimeslotConfirmed: SchedulerEventOverride['timeslotConfirmed'] =
    async (event, connector) => {
      event.preventDefault()

      if (!connector) {
        captureErrorShownToUserEvent({
          message: 'Expected Nylas connector to be defined',
        })

        return
      }

      return bookTimeslotMutation.mutateAsync({ event, connector })
    }

  // This is a workaround for the fact that the NylasScheduler doesn't respect
  // updated props. Changing the `key` forces a re-render of that component.
  // The downside is a flicker as it re-renders, but we'll have to live with it.
  const key = [
    schedulerConfigId,
    primaryParticipant.email,
    primaryParticipant.name,
    dismissedErrorCount,
  ].join('-')

  // const timezone = await nylasSchedulingElementRef.current.getNylasSchedulerStore()

  return (
    <div className="position-relative w-100 d-flex flex-column align-items-center">
      {/* @see https://tinyurl.com/nylas-react */}
      <NylasScheduling
        ref={nylasSchedulingElementRef}
        key={key}
        configurationId={schedulerConfigId}
        mode="app"
        schedulerApiUrl="https://api.eu.nylas.com"
        bookingInfo={{
          primaryParticipant,
          guests,
        }}
        themeConfig={THEME_CONFIG}
        onNylasSchedulerError={(error) => {
          setSchedulerError({
            title: error.detail.notification.title,
            description: error.detail.notification.description,
          })

          captureErrorShownToUserEvent({
            message: `Nylas scheduler error: ${error.detail.notification.title}: ${error.detail.notification.description}`,
            extra: {
              clientDetails: primaryParticipant,
              error: error.detail.notification,
            },
          })
        }}
        className={className}
        nylasBranding={false}
        enableUserFeedback={false}
        defaultSchedulerState={{ showBookingForm: false }}
        localization={{
          en: {
            // We skip Nylas' default confirmation step so change the default
            // button text from 'Next' to 'Schedule meeting'
            nextButton: 'Schedule meeting',
          },
        }}
        eventOverrides={{ timeslotConfirmed: handleTimeslotConfirmed }}
      />

      {schedulerError && (
        <Card
          className="position-absolute mb-3 d-flex flex-column align-items-center justify-content-start text-center p-4 h-100 top-0 mx-auto"
          style={{
            backdropFilter: 'blur(.75rem) grayscale(1)',
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            zIndex: 2,
            width: '785px',
            maxWidth: '100%',
            marginInline: 'auto !important',
          }}
        >
          <div className="pop-in pt-4">
            <h3 className="fs-1 fw-bold">
              <strong>Scheduler error</strong>
            </h3>

            <p style={{ textWrap: 'balance' }}>
              Sorry, something went wrong with the scheduler. Our engineering
              team have been informed. If the problem persists, please try again
              later or <OpenChatButton>contact us</OpenChatButton>.
            </p>

            {/* Will result in a reload of the scheduler */}
            <Button
              onClick={() => {
                setSchedulerError(null)
                setDismissedErrorCount(dismissedErrorCount + 1)
              }}
              size="lg"
            >
              <FontAwesomeIcon icon={faRotateRight} className="me-2" />
              Reload the scheduler
            </Button>

            <Collapsible
              className="mt-3"
              header={<header className="fs-5 mb-2">Error details</header>}
              isCollapsibleEnabled
            >
              <dl>
                <dt>
                  <strong>Title</strong>
                </dt>
                <dd>{schedulerError.title}</dd>

                <dt>
                  <strong>Description</strong>
                </dt>
                <dd>{schedulerError.description}</dd>
              </dl>
            </Collapsible>
          </div>
        </Card>
      )}
    </div>
  )
}

// Make the scheduler look as RQ-like as possible
const THEME_CONFIG: ThemeConfig = {
  '--nylas-primary': 'var(--bs-primary)',
  '--nylas-info': 'var(--bs-info)',
  '--nylas-success': 'var(--bs-success)',
  '--nylas-warning': 'var(--bs-warning)',
  '--nylas-error': 'var(--bs-error)',
  '--nylas-base-100': 'var(--bs-gray-100)',
  '--nylas-base-200': 'var(--bs-gray-200)',
  '--nylas-base-300': 'var(--bs-gray-300)',
  '--nylas-base-400': 'var(--bs-gray-400)',
  '--nylas-base-500': 'var(--bs-gray-500)',
  '--nylas-base-600': 'var(--bs-gray-600)',
  '--nylas-base-700': 'var(--bs-gray-700)',
  '--nylas-base-800': 'var(--bs-gray-800)',
  '--nylas-base-900': 'var(--bs-gray-900)',
  '--nylas-font-family': 'var(--bs-body-font-family)',
  '--nylas-font-size': 'var(--bs-body-font-size)',
  '--nylas-border-radius': 'calc(var(--bs-border-radius) * .5)',
  '--nylas-border-radius-2x': 'calc(var(--bs-border-radius) * 1)',
  '--nylas-border-radius-3x': 'calc(var(--bs-border-radius) * 1.5)',
}

export default NylasScheduler
